
import type React from "react"

import { useState } from "react"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { apiRequest } from "@/lib/api-client"

function isPaymentCurrentlyActive(payment: any, remainingEntries?: number): boolean {
  if (payment.deleted || payment.expired) return false
  if (payment.endDate) {
    if (payment.endDate === 'fixed') {
      return typeof remainingEntries === 'number' && remainingEntries > 0
    }
    return dayjs().isBefore(dayjs(payment.endDate))
  }
  const plan = typeof payment.planId === 'object' ? payment.planId : null
  if (plan && plan.durationType === 'fixed') {
    return typeof remainingEntries === 'number' && remainingEntries > 0
  }
  return false
}

function ActivePlansTable({ user }: { user: any }) {
  const { t } = useTranslation()
  const payments: any[] = Array.isArray(user.payments) ? user.payments : []
  const activePlans = payments.filter((p) => isPaymentCurrentlyActive(p, user.remainingEntries))

  if (activePlans.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic py-2">{t("users.noActivePlans")}</p>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("users.activePlan")}</TableHead>
          <TableHead>{t("users.startDate")}</TableHead>
          <TableHead>{t("users.endDate")}</TableHead>
          <TableHead>{t("users.remaining")}</TableHead>
          <TableHead>{t("users.status")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {activePlans.map((payment) => {
          const plan = typeof payment.planId === 'object' && payment.planId !== null ? payment.planId : null
          const planName = plan?.name ?? t("users.activePlan")
          const isFixed = payment.endDate === 'fixed' || plan?.durationType === 'fixed'
          const startDate = payment.date
            ? new Date(payment.date + "T00:00:00").toLocaleDateString()
            : "-"
          const endDate = isFixed
            ? t("users.fixedPlan")
            : payment.endDate
            ? new Date(payment.endDate).toLocaleDateString()
            : "-"
          const durationLabel = plan?.durationType
            ? plan.durationType === 'fixed'
              ? t("users.fixedPlan")
              : `${plan.duration ?? ''} ${plan.durationType}`
            : "-"

          return (
            <TableRow key={payment._id}>
              <TableCell className="font-medium">{planName}</TableCell>
              <TableCell>{startDate}</TableCell>
              <TableCell>{endDate}</TableCell>
              <TableCell>
                {isFixed
                  ? typeof user.remainingEntries === 'number'
                    ? user.remainingEntries
                    : "-"
                  : <span className="text-muted-foreground text-xs">{durationLabel}</span>
                }
              </TableCell>
              <TableCell>
                <Badge variant="default" className="bg-green-600 hover:bg-green-700">
                  {t("users.active")}
                </Badge>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

interface UserDetailsProps {
  user: any
  onUserUpdated?: () => void
}

export function UserDetails({ user, onUserUpdated }: UserDetailsProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: user.name || "",
    surname: user.surname || "",
    email: user.email || "",
    phone: user.phone || "",
    status: user.status || "active",
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target
    setFormData((prev) => ({ ...prev, [id]: value }))
  }

  const handleStatusChange = (value: string) => {
    setFormData((prev) => ({ ...prev, status: value }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await apiRequest({
        method: "PUT",
        url: `/users/${user._id}`,
        data: formData,
      })

      toast({
        title: t("common.success"),
        description: t("users.userUpdated"),
      })

      setIsEditing(false)

      if (onUserUpdated) {
        onUserUpdated()
      }
    } catch (error) {
      console.error("Failed to update user:", error)
      toast({
        title: t("common.error"),
        description: "Failed to update user",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      {isEditing ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t("users.firstName")}</Label>
              <Input id="name" value={formData.name} onChange={handleChange} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="surname">{t("users.lastName")}</Label>
              <Input id="surname" value={formData.surname} onChange={handleChange} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t("users.email")}</Label>
              <Input id="email" type="email" value={formData.email} onChange={handleChange} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">{t("users.phone")}</Label>
              <Input id="phone" value={formData.phone} onChange={handleChange} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">{t("users.status")}</Label>
              <Select value={formData.status} onValueChange={handleStatusChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t("users.status")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t("users.active")}</SelectItem>
                  <SelectItem value="inactive">{t("users.inactive")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => setIsEditing(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? t("common.saving") : t("users.saveChanges")}
            </Button>
          </div>
        </form>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium text-muted-foreground">{t("users.firstName")}</div>
              <div>{user.name}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">{t("users.lastName")}</div>
              <div>{user.surname}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">{t("users.email")}</div>
              <div>{user.email}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">{t("users.phone")}</div>
              <div>{user.phone || "-"}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">{t("users.status")}</div>
              <div>{user.status === "active" ? t("users.active") : t("users.inactive")}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">{t("users.joinDate")}</div>
              <div>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "-"}</div>
            </div>
          </div>

          {/* Active Plans */}
          <div>
            <div className="text-sm font-semibold mb-2">{t("users.activePlans")}</div>
            <ActivePlansTable user={user} />
          </div>

          <div className="flex justify-end">
            <Button onClick={() => setIsEditing(true)}>{t("common.edit")}</Button>
          </div>
        </div>
      )}
    </div>
  )
}
